import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { Box, Button } from '@mui/material'
import { ChevronRight } from '@mui/icons-material'
import { ResizableBox  } from 'react-resizable'
import debounce from 'lodash.debounce'

import { FileNodeProp } from '../utils/types'
import { CustomHandle, dashBoxStyle } from '../styles/js/Dashboard.style'
import Directories from '../component/userDirectory/Directories'
import { MapWarningTexts } from '../component/imageViewer/helper/uiElements'
import ImageViewer from '../component/imageViewer/ImageViewerV2'
import CompareImageViewer from '../component/imageViewer/CompareImageViewer'
import { DashContext } from "../component/common/context";

const Dashboardv2 = () => {
  const [showTree, setShowTree] = useState<boolean>(true)
  const [compareImage, setCompareImage] = useState<boolean>(false)
  const [nodes, setNodes] = useState<(FileNodeProp | null)[]>([null, null])
  const [showMap, setShowMap] = useState<boolean>(false)

  const handleShowTree = () => setShowTree(prevState => !prevState)

  // eslint-disable-next-line
  const selectSlide = useCallback(debounce((nodeData: FileNodeProp, compare: boolean) => {
    setNodes(prevArray => {
      if (compare) {
        const newArray = [...prevArray]
        if (newArray[0]) newArray[1] = nodeData
        else newArray[0] = nodeData
        return newArray
      } else {
        if (prevArray[0]?.file_id===nodeData.file_id) return prevArray
        else {
          const newArray = [...prevArray]
          newArray[0] = nodeData
          return newArray
        }
      }
    })
  }, 300), [])

  useEffect(() => {
    if (nodes[0]) {
      // This will force unmount the map container and avoid any Leaflet container unmount delay.
      setShowMap(false)
      setTimeout(() => setShowMap(true), 0)
    }
  }, [nodes[0]])  // eslint-disable-line

  // eslint-disable-next-line
  const duplicateSlides = useCallback(debounce(() => {
    setNodes(prevArray => {
      const newArray = [...prevArray]
      newArray[1] = newArray[0]
      return newArray
    })
  }, 300), [])

  const toggleCompareViewer = () => {
    setNodes([null, null])
    setCompareImage(prevState => !prevState)
  }

  const resetMap = () => {
    setShowMap(false)
    setTimeout(() => setShowMap(true), 0)
  }

  const MemoizedDirectory = useMemo(() =>
    <Directories
      handleShowTree={handleShowTree}
      selectSlide={selectSlide}
      toggleCompareViewer={toggleCompareViewer}
      compareImage={compareImage}
      resetMap={resetMap}
    />
  , [compareImage]) // eslint-disable-line

  return (
    <DashContext.Provider value={{selectedNodes: nodes}}>
      <Box sx={dashBoxStyle}>
        <Box className='d-flex'>
          <ResizableBox 
            width={400}
            height={Infinity}
            minConstraints={[400, Infinity]}
            maxConstraints={[1000, Infinity]}
            handle={showTree ? <CustomHandle /> : null}
            className='position-relative'
            style={{ display: showTree ? 'unset' : 'none' }}
          >
            {MemoizedDirectory}
          </ResizableBox>
          {!showTree
            ? <Button sx={{minWidth: 32, padding: 0}} onClick={handleShowTree} aria-label='Open directory'>
              <ChevronRight />
            </Button>
            : null
          }
        </Box>
        {compareImage
          ? <CompareImageViewer slide1={nodes[0]} slide2={nodes[1]} duplicateSlides={duplicateSlides} />
          : nodes[0] && showMap
            ? <ImageViewer key={nodes[0].id} imageProp={nodes[0]} resetMap={resetMap} />
            : <MapWarningTexts text={"Select a slide to view"} />
        }
      </Box>
    </DashContext.Provider>
  )
}

export default Dashboardv2